import { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PlatformLogo } from "@/components/PlatformLogo";
import { Plus, Plugs, Trash, ArrowsClockwise, UploadSimple, PlugsConnected, PencilSimple, LinkSimple } from "@phosphor-icons/react";

const SETTINGS_PLATFORMS = ["mock"];
const PUBLIC_APP_URL = (process.env.REACT_APP_PUBLIC_URL || "https://your-app-domain.com").replace(/\/+$/, "");
const ZENDESK_CALLBACK_URL = `${PUBLIC_APP_URL}/api/connections/zendesk/callback`;
const OAUTH_CALLBACK_URL = `${PUBLIC_APP_URL}/api/connections/oauth/callback`;

export default function Connections() {
  const [connections, setConnections] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState("");
  const [fields, setFields] = useState({});
  const [useTrial, setUseTrial] = useState(false);
  const [secretSet, setSecretSet] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileRefs = useRef({});

  const load = () => api.get("/connections").then((r) => setConnections(r.data));
  useEffect(() => {
    load();
    api.get("/connections/platforms").then((r) => setPlatforms(r.data));
  }, []);

  // Return from an OAuth callback (?connected=<platform> / ?zendesk=connected / ?*_error=...)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const err = p.get("oauth_error") || p.get("zendesk_error");
    if (p.get("connected") || p.get("zendesk") === "connected") {
      const plat = p.get("connected") || "Zendesk";
      toast.success(`${plat} connected — token stored securely.`);
      load();
    } else if (err) {
      toast.error(`Authorization failed: ${err.replace(/_/g, " ")}`);
    }
    if (p.get("connected") || p.get("zendesk") || err) window.history.replaceState({}, "", "/connections");
  }, []);

  const connectOAuth = async (c) => {
    const isZendesk = c.platform === "zendesk";
    const url = isZendesk ? `/connections/${c.id}/zendesk/authorize` : `/connections/${c.id}/oauth/authorize`;
    const t = toast.loading("Starting authorization…");
    try {
      const { data } = await api.post(url);
      toast.dismiss(t);
      window.location.assign(data.authorize_url);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not start authorization", { id: t });
    }
  };

  const meta = platforms.find((p) => p.platform === platform);
  const metaByPlatform = Object.fromEntries(platforms.map((p) => [p.platform, p]));
  const credFields = meta?.credential_fields?.length
    ? meta.credential_fields
    : (meta?.fields || []).map((f) => ({ name: f, label: f.replace(/_/g, " "), secret: /password|secret|token|key/i.test(f) }));

  const resetForm = () => { setName(""); setPlatform(""); setFields({}); setUseTrial(false); setSecretSet({}); setPreviewUrl(null); setEditId(null); };

  const openCreate = () => { resetForm(); setOpen(true); };

  const openEdit = async (c) => {
    resetForm();
    try {
      const { data } = await api.get(`/connections/${c.id}/editable`);
      setEditId(c.id); setName(data.name || ""); setPlatform(data.platform);
      setFields(SETTINGS_PLATFORMS.includes(data.platform) ? (data.settings || {}) : (data.credentials || {}));
      setSecretSet(data.secret_set || {});
      setOpen(true);
    } catch (e) { toast.error("Could not load connection for editing"); }
  };

  // Live "will connect to" preview (debounced) — catches wrong host/subdomain before Test.
  useEffect(() => {
    if (!open || !platform || useTrial) { setPreviewUrl(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/connections/preview-endpoint", { platform, credentials: fields });
        setPreviewUrl(data.url || null);
      } catch (e) { setPreviewUrl(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [open, platform, JSON.stringify(fields), useTrial]);

  const save = async () => {
    setSaving(true);
    try {
      if (editId) {
        const body = SETTINGS_PLATFORMS.includes(platform)
          ? { name, settings: { record_count: Number(fields.record_count || 25), inject_faults: fields.inject_faults !== false && fields.inject_faults !== "false" } }
          : { name, credentials: fields };
        await api.patch(`/connections/${editId}`, body);
        toast.success("Connection updated");
      } else {
        const useSettings = SETTINGS_PLATFORMS.includes(platform);
        let payload;
        if (useTrial) {
          payload = { name, platform, credentials: {}, settings: { use_trial: true } };
        } else if (useSettings) {
          payload = {
            name, platform, credentials: {},
            settings: { record_count: Number(fields.record_count || 25), inject_faults: fields.inject_faults !== "false" },
          };
        } else {
          payload = { name, platform, credentials: fields, settings: {} };
        }
        await api.post("/connections", payload);
        toast.success("Connection created");
      }
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const test = async (id) => {
    const t = toast.loading("Testing connection…");
    try {
      const r = await api.post(`/connections/${id}/test`);
      const ok = r.data.status === "connected" || r.data.ok;
      if (ok) toast.success(r.data.message || "Connected", { id: t });
      else toast.error(r.data.message || "Connection test failed", { id: t });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Test failed", { id: t });
    }
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this connection?")) return;
    await api.delete(`/connections/${id}`); toast.success("Deleted"); load();
  };

  const disable = async (id) => { await api.patch(`/connections/${id}/disable`); load(); };

  const upload = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    const p = api.post(`/connections/${id}/upload-csv`, fd);
    toast.promise(p, { loading: "Uploading CSV…", success: (r) => `Loaded ${r.data.row_count} rows`, error: "Upload failed" });
    try { await p; } catch (e) {} load();
  };

  const uploadExcel = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    const p = api.post(`/connections/${id}/upload-excel`, fd);
    toast.promise(p, { loading: "Uploading Excel…", success: (r) => `Loaded ${r.data.sheets.length} sheet(s), ${r.data.row_count} rows`, error: "Upload failed" });
    try { await p; } catch (e) {} load();
  };

  return (
    <Layout>
      <PageHeader title="Connections" subtitle="Manage source & destination system connections"
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <Button data-testid="new-connection-btn" onClick={openCreate}
              className="rounded-xl bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200 gap-2">
              <Plus size={16} /> New Connection
            </Button>
            <DialogContent className="rounded-xl max-h-[90vh] flex flex-col">
              <DialogHeader><DialogTitle className="font-head">{editId ? "Edit Connection" : "New Connection"}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2 overflow-y-auto flex-1 -mx-1 px-1">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500">Connection Name</Label>
                  <Input data-testid="conn-name" value={name} onChange={(e) => setName(e.target.value)}
                    className="mt-1 rounded-xl" placeholder="Production Salesforce" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500">Platform</Label>
                  <Select value={platform} disabled={!!editId} onValueChange={(v) => { setPlatform(v); setFields({}); setUseTrial(false); }}>
                    <SelectTrigger data-testid="conn-platform" className="mt-1 rounded-xl"><SelectValue placeholder="Select platform" /></SelectTrigger>
                    <SelectContent>
                      {platforms.map((p) => (
                        <SelectItem key={p.platform} value={p.platform}>
                          <div className="flex items-center gap-2">
                            <PlatformLogo platform={p.platform} size={16} logoUrl={p.logo_url} baseUrl={p.base_url} /> {p.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {meta?.trial && !editId && (
                  <label data-testid="use-trial-toggle" className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3 cursor-pointer">
                    <input type="checkbox" checked={useTrial} onChange={(e) => setUseTrial(e.target.checked)} className="mt-0.5" />
                    <span className="text-xs text-stone-700 leading-relaxed">
                      <strong>Use trial / sandbox account</strong> — connect instantly with no credentials to evaluate this connector.
                    </span>
                  </label>
                )}
                {!useTrial && credFields.map((f) => (
                  <div key={f.name}>
                    <Label className="text-xs uppercase tracking-wider text-stone-500">{f.label}</Label>
                    {f.name.endsWith("_json") ? (
                      <textarea data-testid={`conn-field-${f.name}`}
                        value={fields[f.name] || ""} onChange={(e) => setFields({ ...fields, [f.name]: e.target.value })}
                        placeholder={editId && f.secret && secretSet[f.name] ? "Leave blank to keep current" : "Paste the full Service Account JSON here"}
                        className="mt-1 w-full h-28 rounded-xl border border-stone-200 p-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#C2410C]" />
                    ) : (
                      <Input data-testid={`conn-field-${f.name}`}
                        type={f.secret ? "password" : "text"}
                        value={fields[f.name] || ""} onChange={(e) => setFields({ ...fields, [f.name]: e.target.value })}
                        placeholder={editId && f.secret && secretSet[f.name] ? "•••••• (leave blank to keep current)" : ""}
                        className="mt-1 rounded-xl font-mono text-sm" />
                    )}
                  </div>
                ))}
                {!useTrial && previewUrl && (
                  <div data-testid="endpoint-preview" className="flex items-start gap-2 text-xs bg-stone-50 border border-stone-200 rounded-xl p-3">
                    <LinkSimple size={15} className="shrink-0 mt-0.5 text-[#C2410C]" />
                    <span className="text-stone-600 min-w-0">
                      API base this connection will call{" "}
                      <span className="text-stone-400">(not a web page — opening it in a browser won't work)</span>:{" "}
                      <span className="font-mono text-stone-900 break-all">{previewUrl}</span>
                    </span>
                  </div>
                )}
                {platform === "csv" && !editId && <p className="text-xs text-stone-500">Upload the CSV file after creating the connection.</p>}
                {platform === "salesforce" && (
                  <div className="text-xs text-stone-600 bg-orange-50 border border-orange-100 rounded-xl p-3 leading-relaxed">
                    <strong>Recommended (Client Credentials):</strong> fill only <strong>Client ID</strong>, <strong>Client Secret</strong>, and set <strong>Login URL</strong> to your My Domain URL (e.g. <span className="font-mono">https://yourorg.my.salesforce.com</span>). Leave Username / Password / Security Token blank. Your Connected App must have "Enable Client Credentials Flow" with a Run As user.
                  </div>
                )}
                {platform === "google_sheets" && (
                  <div className="text-xs text-stone-600 bg-orange-50 border border-orange-100 rounded-xl p-3 leading-relaxed space-y-1">
                    <div><strong>Spreadsheet ID</strong> = the long ID in your sheet URL between <span className="font-mono">/d/</span> and <span className="font-mono">/edit</span>.</div>
                    <div>Then fill <strong>one</strong> of: <strong>Service Account JSON</strong> (recommended — private sheets, read &amp; write; share the sheet with the JSON's <span className="font-mono">client_email</span>) <em>or</em> <strong>API Key</strong> (read-only, only for "Anyone with the link" sheets). Leave the other blank.</div>
                    <div>Full step-by-step: see the <strong>Help → Google Sheets</strong> tab.</div>
                  </div>
                )}
                {platform === "sap_odata" && (
                  <div className="text-xs text-stone-600 bg-orange-50 border border-orange-100 rounded-xl p-3 leading-relaxed space-y-1">
                    <div><strong>Service URL</strong> = the full OData service root, e.g. <span className="font-mono">https://host/sap/opu/odata/sap/API_BUSINESS_PARTNER</span> (S/4HANA) or your SuccessFactors OData endpoint.</div>
                    <div>Then authenticate with <strong>one</strong> of: <strong>Username + Password</strong> (Basic — works for SuccessFactors and Gateway) <em>or</em> <strong>OAuth URL + Client ID + Client Secret</strong> (client-credentials — S/4HANA Cloud communication arrangement). Leave the unused fields blank.</div>
                    <div>Schema (entity sets &amp; fields) is discovered automatically from the service <span className="font-mono">$metadata</span>. Works with OData v2 and v4.</div>
                  </div>
                )}
                {platform === "zendesk" && (
                  <div className="text-xs text-stone-600 bg-orange-50 border border-orange-100 rounded-xl p-3 leading-relaxed space-y-1">
                    <div>Zendesk uses <strong>OAuth</strong>. Enter your <strong>Subdomain</strong> (just <span className="font-mono">acme</span>), <strong>OAuth Client ID</strong> and <strong>OAuth Client Secret</strong> from your Zendesk OAuth client, then click <strong>Create Connection</strong>.</div>
                    <div>Next, on the connection card click <strong>Connect with Zendesk</strong> to authorize — we fetch and auto-refresh the token for you.</div>
                    <div>Register your app's <strong>Redirect URL</strong> in your Zendesk OAuth client: <span className="font-mono break-all">{ZENDESK_CALLBACK_URL}</span> (replace the domain with your own — in preview use your browser's address). Full steps: <strong>Help → Zendesk</strong>.</div>
                  </div>
                )}
                {platform === "zoho_crm" && (
                  <div className="text-xs text-stone-600 bg-orange-50 border border-orange-100 rounded-xl p-3 leading-relaxed space-y-1">
                    <div>Zoho CRM uses <strong>OAuth</strong> (India data center). In the <strong>Zoho API Console</strong> (api-console.zoho.in) create a <strong>Server-based Application</strong>, then paste its <strong>Client ID</strong> &amp; <strong>Client Secret</strong> here and click <strong>Create Connection</strong>.</div>
                    <div>Next, on the connection card click <strong>Connect Zoho CRM</strong> to authorize — we fetch and auto-refresh the token for you.</div>
                    <div>Register this <strong>Authorized Redirect URI</strong> in the Zoho API Console: <span className="font-mono break-all">{OAUTH_CALLBACK_URL}</span> (replace the domain with your own — in preview use your browser's address).</div>
                  </div>
                )}
                {platform === "airtable" && (
                  <div className="text-xs text-stone-600 bg-orange-50 border border-orange-100 rounded-xl p-3 leading-relaxed space-y-1">
                    <div>Create a <strong>Personal Access Token</strong> at airtable.com/create/tokens with scopes <span className="font-mono">data.records:read</span>, <span className="font-mono">data.records:write</span> and <span className="font-mono">schema.bases:read</span>, and grant it access to your base.</div>
                    <div><strong>Base ID</strong> starts with <span className="font-mono">app…</span> (find it at airtable.com/api). In <strong>Table names</strong>, list the tables to migrate exactly as named, comma-separated (e.g. <span className="font-mono">Contacts, Companies</span>).</div>
                  </div>
                )}
                {platform === "freshsales" && (
                  <div className="text-xs text-stone-600 bg-orange-50 border border-orange-100 rounded-xl p-3 leading-relaxed space-y-1">
                    <div>Find your <strong>API Key</strong> in Freshsales under <strong>Personal Settings → API Settings</strong>. Domain is the <span className="font-mono">yourco</span> in <span className="font-mono">yourco.myfreshworks.com</span>.</div>
                    <div>Listing records needs a <strong>View ID</strong>: open Contacts/Accounts/Deals, pick a view (e.g. "All Contacts"), and copy the number from the URL into the matching View ID field.</div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button data-testid="conn-save" onClick={save} disabled={!name || !platform || saving}
                  className="rounded-xl bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200">
                  {saving ? "Saving…" : editId ? "Save Changes" : "Create Connection"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-8">
        {connections.length === 0 ? (
          <div className="bg-white border border-dashed border-stone-300 rounded-xl py-20 text-center">
            <PlugsConnected size={40} className="mx-auto text-stone-300" />
            <p className="text-stone-500 mt-4 text-sm">No connections yet. Create your first source or destination.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {connections.map((c) => (
              <div key={c.id} data-testid={`connection-card-${c.id}`}
                className="bg-white border border-stone-200 rounded-2xl shadow-card p-5 flex flex-col">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <PlatformLogo platform={c.platform} size={26} logoUrl={metaByPlatform[c.platform]?.logo_url} baseUrl={metaByPlatform[c.platform]?.base_url} />
                    <div>
                      <div className="text-xs uppercase tracking-[0.15em] text-stone-400 font-bold">{c.platform}</div>
                      <div className="font-head font-semibold text-stone-950 mt-0.5">{c.name}</div>
                    </div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
                <div className="text-xs text-stone-500 mt-3 font-mono">
                  Last connected: {c.last_connected ? new Date(c.last_connected).toLocaleString() : "never"}
                </div>
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-stone-100">
                  <Button data-testid={`test-conn-${c.id}`} size="sm" variant="outline"
                    onClick={() => test(c.id)} className="rounded-xl gap-1.5 text-xs">
                    <Plugs size={14} /> Test
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => test(c.id)} className="rounded-xl gap-1.5 text-xs">
                    <ArrowsClockwise size={14} /> Reconnect
                  </Button>
                  <Button data-testid={`edit-conn-${c.id}`} size="sm" variant="outline"
                    onClick={() => openEdit(c)} className="rounded-xl gap-1.5 text-xs">
                    <PencilSimple size={14} /> Edit
                  </Button>
                  {metaByPlatform[c.platform]?.oauth && (
                    <Button data-testid={`oauth-connect-${c.id}`} size="sm"
                      onClick={() => connectOAuth(c)}
                      className="rounded-xl gap-1.5 text-xs bg-stone-900 hover:bg-stone-800 text-white">
                      <LinkSimple size={14} /> {c.status === "connected" ? "Re-authorize" : `Connect ${metaByPlatform[c.platform]?.label || ""}`}
                    </Button>
                  )}
                  {c.platform === "csv" && (
                    <>
                      <input type="file" accept=".csv" hidden ref={(el) => (fileRefs.current[c.id] = el)}
                        onChange={(e) => e.target.files[0] && upload(c.id, e.target.files[0])} />
                      <Button data-testid={`upload-csv-${c.id}`} size="sm" variant="outline"
                        onClick={() => fileRefs.current[c.id]?.click()} className="rounded-xl gap-1.5 text-xs">
                        <UploadSimple size={14} /> Upload CSV
                      </Button>
                    </>
                  )}
                  {c.platform === "excel" && (
                    <>
                      <input type="file" accept=".xlsx,.xls" hidden ref={(el) => (fileRefs.current["xls-" + c.id] = el)}
                        onChange={(e) => e.target.files[0] && uploadExcel(c.id, e.target.files[0])} />
                      <Button data-testid={`upload-excel-${c.id}`} size="sm" variant="outline"
                        onClick={() => fileRefs.current["xls-" + c.id]?.click()} className="rounded-xl gap-1.5 text-xs">
                        <UploadSimple size={14} /> Upload Excel
                      </Button>
                    </>
                  )}
                  <Button data-testid={`delete-conn-${c.id}`} size="sm" variant="ghost"
                    onClick={() => del(c.id)} className="rounded-xl gap-1.5 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-auto">
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
